/**
 * Task 204 — Corpus Reader "reading record" (a saved reading point).
 *
 * Design decision (documented for the next reader): this is a SINGLE current
 * reading point, not a multi-bookmark list. The spec explicitly allows "a single
 * 'current reading point' rather than multiple bookmarks", and that fits Bow and
 * Arrow's quiet, study-dense aesthetic best — no bookmark-manager UI, no list to
 * position, no layout shift. Saving a new point replaces the previous one. The
 * persisted shape still carries an array + `activeBookmarkId` so a future
 * multi-bookmark v2 is a non-breaking extension (we just lift MAX_BOOKMARKS).
 *
 * Persistence posture mirrors state/session-persistence.ts: `sessionStorage`
 * only (survives a reload in the same tab, clears when the tab/session ends),
 * never `localStorage`; no server writes; only a short snippet + location
 * metadata is stored (never an entire selected section). The pure
 * parse/serialize/sanitize helpers below are unit-tested by
 * reading-record.smoke.ts with a stub store, so the rules are verified without a
 * browser.
 */

export const READING_RECORD_SCHEMA_VERSION = 1 as const;
/** v1 keeps a single current reading point. Raise to enable a bookmark list. */
export const MAX_BOOKMARKS = 1;
/** Hard cap on the stored snippet — we keep a short excerpt, never the section. */
export const SNIPPET_MAX = 140;

const STORAGE_KEY = "bow-and-arrow:reading-record";

export interface ReadingBookmark {
  id: string;
  book: string;
  chapter: number;
  anchor?: string;
  snippet: string;
  createdAt: string;
  scrollY?: number;
}

export interface ReadingRecord {
  schemaVersion: typeof READING_RECORD_SCHEMA_VERSION;
  bookmarks: ReadingBookmark[];
  activeBookmarkId?: string;
}

export const EMPTY_RECORD: ReadingRecord = {
  schemaVersion: READING_RECORD_SCHEMA_VERSION,
  bookmarks: [],
};

/** Collapse whitespace and clamp to a short excerpt with an ellipsis. */
export function clampSnippet(raw: string): string {
  const normalized = raw.replace(/\s+/g, " ").trim();
  if (normalized.length <= SNIPPET_MAX) return normalized;
  // Cut on a word boundary when one is near the limit, else hard-cut.
  const slice = normalized.slice(0, SNIPPET_MAX);
  const lastSpace = slice.lastIndexOf(" ");
  const head = lastSpace > SNIPPET_MAX * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${head.trimEnd()}…`;
}

/** Validate + narrow one stored bookmark, or null if unusable. */
export function sanitizeBookmark(raw: unknown): ReadingBookmark | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== "string" || o.id.length === 0) return null;
  if (typeof o.book !== "string" || o.book.length === 0) return null;
  if (typeof o.chapter !== "number" || !Number.isFinite(o.chapter)) return null;
  if (typeof o.snippet !== "string") return null;
  if (typeof o.createdAt !== "string" || o.createdAt.length === 0) return null;
  const bookmark: ReadingBookmark = {
    id: o.id,
    book: o.book,
    chapter: o.chapter,
    snippet: clampSnippet(o.snippet),
    createdAt: o.createdAt,
  };
  if (typeof o.anchor === "string" && o.anchor.length > 0) bookmark.anchor = o.anchor;
  if (typeof o.scrollY === "number" && Number.isFinite(o.scrollY) && o.scrollY >= 0) {
    bookmark.scrollY = o.scrollY;
  }
  return bookmark;
}

/** Cap to MAX_BOOKMARKS, keeping the most recent (the head is newest). */
export function capBookmarks(bookmarks: ReadingBookmark[]): ReadingBookmark[] {
  return bookmarks.slice(0, MAX_BOOKMARKS);
}

/** Rebuild a clean record from arbitrary parsed JSON. A tampered/old payload
 *  degrades to EMPTY_RECORD rather than crashing the reader. */
export function reviveRecord(raw: unknown): ReadingRecord {
  if (typeof raw !== "object" || raw === null) return EMPTY_RECORD;
  const o = raw as Record<string, unknown>;
  if (o.schemaVersion !== READING_RECORD_SCHEMA_VERSION) return EMPTY_RECORD;
  if (!Array.isArray(o.bookmarks)) return EMPTY_RECORD;
  const bookmarks = capBookmarks(
    o.bookmarks.map(sanitizeBookmark).filter((b): b is ReadingBookmark => b !== null),
  );
  if (bookmarks.length === 0) return EMPTY_RECORD;
  const activeRaw = typeof o.activeBookmarkId === "string" ? o.activeBookmarkId : undefined;
  // Only keep the active id if it still points at a surviving bookmark; else
  // fall back to the newest so "jump back" always has a valid target.
  const active = bookmarks.some((b) => b.id === activeRaw)
    ? activeRaw
    : bookmarks[0]?.id;
  const record: ReadingRecord = {
    schemaVersion: READING_RECORD_SCHEMA_VERSION,
    bookmarks,
  };
  if (active !== undefined) record.activeBookmarkId = active;
  return record;
}

/** The active (current) reading point, or null. */
export function activeBookmark(record: ReadingRecord): ReadingBookmark | null {
  if (record.activeBookmarkId === undefined) return record.bookmarks[0] ?? null;
  return record.bookmarks.find((b) => b.id === record.activeBookmarkId) ?? null;
}

/** Parse a raw stored string into a clean record (pure; for the smoke). */
export function parseRecord(raw: string | null): ReadingRecord {
  if (raw === null) return EMPTY_RECORD;
  try {
    return reviveRecord(JSON.parse(raw));
  } catch {
    return EMPTY_RECORD;
  }
}

// ---- storage I/O (sessionStorage, best-effort) ----------------------------

function getStore(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage ?? null;
  } catch {
    return null; // privacy mode / disabled storage
  }
}

export function loadRecord(store: Storage | null = getStore()): ReadingRecord {
  if (store === null) return EMPTY_RECORD;
  try {
    return parseRecord(store.getItem(STORAGE_KEY));
  } catch {
    return EMPTY_RECORD;
  }
}

export function saveRecord(record: ReadingRecord, store: Storage | null = getStore()): void {
  if (store === null) return;
  try {
    if (record.bookmarks.length === 0) {
      store.removeItem(STORAGE_KEY);
      return;
    }
    store.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Non-fatal — the reading record is a convenience, never a correctness dep.
  }
}
